/**
 * How old an alert result is, and how long the client may keep serving it.
 *
 * Every alert read carries `refreshed_at`: when the backend computed the result
 * being served, as ISO 8601 UTC, or `null` for "computed, time unknown" (rows
 * written before the field existed, legacy fire). It is NOT the date of the
 * newest detection. Contract: `alert-refreshed-at` (environmental).
 *
 * Vue-free and OL-free so the rules can be unit-tested, same as
 * `zone-batch.client.ts`.
 */

/**
 * Floor on how long any computed answer is served from cache, so a backend
 * that is itself behind (a result already older than its cadence) is re-read
 * every few minutes rather than on every call. Matches the React client's
 * TanStack `staleTime`.
 */
export const MIN_ALERT_CACHE_MS = 5 * 60_000;

const HOUR_MS = 60 * 60_000;

/** How often each product's backend recomputes a zone's result. */
export const ALERT_CADENCE_MS = {
  fire: 3 * HOUR_MS,
  deforestation: 24 * HOUR_MS,
} as const;

/**
 * `refreshed_at` off a raw response entry. A missing key, a non-object and
 * anything that is not a parseable time all read as unknown.
 */
function timestampOf(entry: unknown, key: 'checked_at' | 'refreshed_at'): string | null {
  if (typeof entry !== 'object' || entry === null || !(key in entry)) {
    return null;
  }
  const value = (entry as Record<string, unknown>)[key];
  return typeof value === 'string' && Number.isFinite(Date.parse(value)) ? value : null;
}

export function checkedAtOf(entry: unknown): string | null {
  return timestampOf(entry, 'checked_at');
}

export function refreshedAtOf(entry: unknown): string | null {
  return timestampOf(entry, 'refreshed_at');
}

export function alertFreshnessAt(refreshedAt: string | null | undefined, checkedAt: string | null | undefined): string | null {
  return checkedAt != null && Number.isFinite(Date.parse(checkedAt)) ? checkedAt : refreshedAt ?? null;
}

/**
 * Epoch ms after which a cached answer must be re-read.
 *
 * - Not computed (`uncomputed`): stale at once. It is still stored, because the
 *   UI shows the state, but it must never answer a later read.
 * - Time unknown: the floor. Treated as possibly old, as the contract allows.
 * - Otherwise: when the backend's next recompute is due, but no sooner than the
 *   floor and no later than one cadence from now (a future `refreshed_at` from
 *   clock skew must not pin the entry).
 */
export function alertStaleAfter(opts: {
  refreshedAt: string | null;
  fetchedAt: number;
  cadenceMs: number;
  computed: boolean;
}): number {
  const { refreshedAt, fetchedAt, cadenceMs, computed } = opts;
  if (!computed) {
    return fetchedAt;
  }
  const floor = fetchedAt + MIN_ALERT_CACHE_MS;
  if (refreshedAt === null) {
    return floor;
  }
  const nextRecompute = Math.min(Date.parse(refreshedAt) + cadenceMs, fetchedAt + cadenceMs);
  return Math.max(floor, nextRecompute);
}

/**
 * The oldest known `refreshed_at` of several zones, for a view that shows them
 * as one result. The oldest, so one lagging child cannot hide behind the rest.
 */
export function oldestRefreshedAt(values: (string | null | undefined)[]): string | null {
  let oldest: string | null = null;
  let oldestMs = Infinity;
  for (const value of values) {
    const ms = value == null ? NaN : Date.parse(value);
    if (ms < oldestMs) {
      oldest = value ?? null;
      oldestMs = ms;
    }
  }
  return oldest;
}

/**
 * Older than about two missed recomputes: the backend's own staleness alarm
 * threshold, and the point where the age should stop reading as routine.
 */
export function isAlertResultStale(refreshedAt: string, type: 'fire' | 'deforestation', now: number): boolean {
  const maxAgeMs = (type === 'deforestation' ? 26 : 6) * HOUR_MS;
  return now - Date.parse(refreshedAt) > maxAgeMs;
}

/** One formatter per locale: the ages re-render on every clock tick. */
const relativeTimeFormats = new Map<string, Intl.RelativeTimeFormat>();

/** "3 hours ago", in the given locale. Minutes under an hour, days from two days. */
export function formatAlertAge(refreshedAt: string, now: number, locale: string): string {
  const elapsed = Math.max(0, now - Date.parse(refreshedAt));
  let format = relativeTimeFormats.get(locale);
  if (!format) {
    format = new Intl.RelativeTimeFormat(locale, { numeric: 'always' });
    relativeTimeFormats.set(locale, format);
  }
  if (elapsed < HOUR_MS) {
    return format.format(-Math.max(1, Math.round(elapsed / 60_000)), 'minute');
  }
  if (elapsed < 48 * HOUR_MS) {
    return format.format(-Math.round(elapsed / HOUR_MS), 'hour');
  }
  return format.format(-Math.round(elapsed / (24 * HOUR_MS)), 'day');
}
